import { useState } from 'react';
import { Link } from 'react-router-dom';
import { EVENT_ROADMAP } from '../data/events';

export function EventsPage() {
  const [activeTab, setActiveTab] = useState<'all' | 'preserved' | 'classified'>('all');

  const displayedEvents = EVENT_ROADMAP.filter((item) => {
    if (activeTab === 'preserved') return item.status === 'completed';
    if (activeTab === 'classified') return item.status === 'classified';
    return true;
  });

  return (
    <div className="events-page-root">
      {/* Cinematic Ambient Atmosphere */}
      <div className="events-ambient-glow" aria-hidden="true" />

      {/* Hero Section */}
      <section className="events-hero">
        <div className="container">
          <div className="events-badge-pill">
            <span className="live-radar-dot" />
            <span>SOVEREIGN ARCHIVE & ANOMALY TRACKER</span>
          </div>

          <h1 className="events-title">
            SEASON 3 <em>EVENT ROADMAP</em>
          </h1>

          <p className="events-subtitle">
            Chronicles of past sovereign milestones and encrypted future operations. 
            From maritime colosseums to upcoming classified frontiers, track our realm's living legacy.
          </p>

          {/* Interactive Navigation Filter */}
          <div className="events-filter-bar" role="tablist">
            <button
              type="button"
              className={`events-filter-btn ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All Milestones
            </button>
            <button
              type="button"
              className={`events-filter-btn ${activeTab === 'preserved' ? 'active' : ''}`}
              onClick={() => setActiveTab('preserved')}
            >
              Preserved Realms (1)
            </button>
            <button
              type="button"
              className={`events-filter-btn ${activeTab === 'classified' ? 'active' : ''}`}
              onClick={() => setActiveTab('classified')}
            >
              Encrypted Anomaly Path (3)
            </button>
          </div>
        </div>
      </section>

      {/* Roadmap Timeline Stream */}
      <section className="events-roadmap-container">
        <div className="container">
          <div className="roadmap-stream">
            {displayedEvents.map((event, index) => {
              const isClassified = event.status === 'classified';
              const isCompleted = event.status === 'completed';

              return (
                <div
                  key={event.id}
                  className={`roadmap-milestone ${isClassified ? 'milestone-classified' : 'milestone-revealed'}`}
                >
                  {/* Left Column: Timeline Node */}
                  <div className="milestone-track-col">
                    <div className={`milestone-node ${isCompleted ? 'node-revealed' : 'node-encrypted'}`}>
                      {isCompleted ? (
                        <span className="node-icon">✓</span>
                      ) : (
                        <span className="node-icon">?</span>
                      )}
                    </div>
                    {/* Glowing Connector Line with Cinematic Fog Fade */}
                    {index < displayedEvents.length - 1 && (
                      <div className={`milestone-connector ${isClassified ? 'connector-fog' : 'connector-solid'}`} />
                    )}
                  </div>

                  {/* Right Column: Card Body */}
                  <div className="milestone-content-col">
                    {isClassified ? (
                      /* Cinematic Blurred / Redacted Classified Operation Card */
                      <div className="classified-card-wrapper">
                        <div className="classified-shimmer-sweep" />
                        <div className="classified-card-content">
                          <div className="classified-header">
                            <span className="classified-stamp">
                              <span className="lock-glyph">🔒</span>
                              <span>{event.badge}</span>
                            </span>
                            <span className="confidential-seal">TOP SECRET // EYES ONLY</span>
                          </div>

                          <div className="classified-body-blur">
                            <h2 className="classified-heading">
                              <span className="redacted-blur-box">####################</span>
                            </h2>
                            <h3 className="classified-subheading">
                              <span className="redacted-blur-box small">############ ######## #######</span>
                            </h3>
                            <p className="classified-paragraph">
                              <span className="redacted-blur-box text">
                                Encrypted telemetry detected by frontier orbital observatories. 
                                High Council clearance is currently restricted until the designated timeline threshold.
                              </span>
                            </p>
                          </div>

                          <div className="classified-footer-bar">
                            <div className="classified-status-row">
                              <span className="pulse-warning-dot" />
                              <span>Signal Status: <b>Awaiting Chronological Clearance</b></span>
                            </div>
                            <span className="classified-lock-btn">
                              <span>Locked</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Revealed Preserved Event Card */
                      <div className="revealed-event-card">
                        <div className="revealed-card-media">
                          <img
                            src={event.thumbnail}
                            alt={event.title}
                            className="revealed-card-img"
                            loading="lazy"
                          />
                          <div className="revealed-media-overlay" />
                          <div className="revealed-badges-top">
                            <span className="status-badge-verified">
                              <span className="badge-glow-dot" />
                              <span>{event.badge}</span>
                            </span>
                            <span className="photo-count-badge">
                              📸 {event.galleryCount} 4K Photos
                            </span>
                          </div>
                        </div>

                        <div className="revealed-card-details">
                          <div className="revealed-location-row">
                            <span className="loc-pin">📍</span>
                            <span>{event.location}</span>
                          </div>

                          <h2 className="revealed-card-title">{event.title}</h2>
                          <h3 className="revealed-card-tagline">{event.subtitle}</h3>
                          <p className="revealed-card-summary">{event.description}</p>

                          {event.highlights && (
                            <div className="revealed-highlights-grid">
                              {event.highlights.map((hl) => (
                                <div key={hl} className="highlight-item">
                                  <span className="highlight-star">✦</span>
                                  <span>{hl}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="revealed-card-action-bar">
                            <Link to={`/events/${event.slug}`} className="explore-event-cta">
                              <span>Enter Event Showcase & 4K Gallery</span>
                              <span className="cta-arrow">→</span>
                            </Link>

                            <div className="in-game-teleport-note">
                              <span>In-Game: Right-click <b>Event Manager</b> at Grand Hub</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Discord Community Proposals */}
      <section className="events-discord-section">
        <div className="container">
          <div className="events-discord-banner">
            <div className="discord-banner-icon">⚔️</div>
            <div className="discord-banner-text">
              <h3>Propose a Realm Event or Tournament</h3>
              <p>Have an epic architectural contest, faction war, or festival idea? Submit proposals directly to the Council on Discord.</p>
            </div>
            <a
              href="https://discord.gg/hAqvebcSh3"
              target="_blank"
              rel="noreferrer"
              className="events-discord-btn"
            >
              <span>Submit to Council</span>
              <i>→</i>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
